import { Link } from 'react-router-dom';
import { formatCalendarDate, formatInstantDate } from '../../lib/format';
import { isKnownState, SPACE_ITEM_STATES, type SpaceItem, type SpaceKind } from './spaces-api';

/**
 * ONE MEETING TOPIC OR RESEARCH ITEM.
 *
 * 🔴🔴 IT MUST NOT LOOK LIKE A TICKET, and that is a requirement rather than a preference. db/001:
 * *"Deliberately NOT tickets with a label: they have no branch, no MR, no sprint."* If a topic reads
 * as dev work, somebody will look for its branch, ask why it is not in the sprint, or move it on a
 * board — all of which are questions this entity has no answer to.
 *
 * ⇒ The visual grammar is deliberately DIFFERENT from `TicketRow`:
 *   · a dashed left rule and a note-card body, not a dense table row
 *   · no key chip (`APFS-441`), because it has no key
 *   · no status chip from the workflow vocabulary — its four states are its own
 *   · no priority icon, no story points, no assignee avatar
 *   · the ONE place ticket grammar appears is the promotion link, which is exactly the moment the
 *     item HAS become dev work
 */
export function SpaceItemCard({
  item,
  kind,
  onPromote,
  onSetState,
  busy,
  error,
}: {
  item: SpaceItem;
  kind: SpaceKind | string;
  onPromote: () => void;
  onSetState: (state: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const promoted = item.promoted_ticket_id !== null;

  return (
    <li
      aria-label={item.title}
      data-promoted={promoted ? 'true' : 'false'}
      className="border-l-4 border-dashed border-[color:var(--color-line)] bg-[color:var(--color-surface-raised)] px-3 py-3 sm:px-4"
    >
      <div className="flex flex-wrap items-start gap-2">
        <span className="rounded-full border border-[color:var(--color-line)] px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase">
          {kind === 'meeting' ? '🗣 Meeting topic' : '🔬 Research item'}
        </span>
        <StateChip state={item.state} />
        <span className="flex-1" />
        {item.due_on ? (
          <span className="text-xs text-[color:var(--color-ink-muted)]">
            {/* ⛔ `due_on` is a DATE (db/001) — a day, not a moment. Never the instant formatter. */}
            due {formatCalendarDate(item.due_on)}
          </span>
        ) : null}
      </div>

      <h3 className="mt-1.5 text-sm font-semibold text-[color:var(--color-ink)]">{item.title}</h3>
      {item.body ? (
        <p className="mt-1 text-sm whitespace-pre-wrap text-[color:var(--color-ink-muted)]">
          {item.body}
        </p>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-[color:var(--color-ink-muted)]">
        <span>{item.owner ? `owner: ${item.owner}` : 'no owner'}</span>
        {/*
          ⭐ SAID OUT LOUD, on every card. The absence of a sprint is a PROPERTY of this entity, not a
          field somebody forgot to fill in — and a reader who has spent all day in the backlog needs
          telling once per card, not once per page.
        */}
        <span className="italic">no branch · no MR · no sprint</span>
      </div>

      {promoted ? (
        /**
         * 🔴 THE BRIDGE, AND THE ONLY PLACE TICKET GRAMMAR APPEARS.
         * ⭐ `promoted_ticket_key` is assembled server-side from `project.key || '-' || number`, so
         * `TBS-41` is the API's own string rather than something this row guessed from a project it
         * does not know it belongs to. It is null only when the item is not promoted, which is this
         * branch's own condition — the `??` is belt and braces against a deployment that omits it.
         */
        <p className="mt-2 text-sm">
          <span aria-hidden="true">↳ </span>
          <Link
            to={`/backlog?ticket=${item.promoted_ticket_id}`}
            className="font-semibold text-[color:var(--color-accent)] underline"
          >
            {item.promoted_ticket_key ?? `ticket #${item.promoted_ticket_id}`}
          </Link>
          <span className="ml-1 text-[color:var(--color-ink-muted)]">
            {/* ⭐ `promoted_at` is TIMESTAMPTZ (db/012) — an instant, so local time is correct. */}
            — promoted{item.promoted_at ? ` ${formatInstantDate(item.promoted_at)}` : ''}; the work
            now lives on the ticket
          </span>
        </p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onPromote}
            className="h-8 rounded border border-[color:var(--color-accent)] px-3 text-xs font-semibold text-[color:var(--color-accent)] disabled:opacity-50"
          >
            Promote to ticket
          </button>
          {/*
            ⛔ STILL NO DELETE, although `DELETE /space-items/{id}` exists. `dropped` is what
            "we are not doing this" means here, and it leaves the decision readable — the same rule
            the vocabularies follow, for the same reason. Removing the row instead would erase the
            fact that the topic was ever raised.
          */}
          {SPACE_ITEM_STATES.filter((state) => state !== item.state).map((state) => (
            <button
              key={state}
              type="button"
              disabled={busy}
              onClick={() => onSetState(state)}
              className="h-8 rounded border border-[color:var(--color-line)] px-3 text-xs disabled:opacity-50"
            >
              Mark {state}
            </button>
          ))}
        </div>
      )}

      {error ? (
        <p
          role="alert"
          className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1.5 text-xs text-red-900 dark:border-red-900/60 dark:bg-red-950/50 dark:text-red-100"
        >
          {error}
        </p>
      ) : null}
    </li>
  );
}

/**
 * ⚠️ NOT a `StatusChip`. Reusing the workflow chip would give a topic the colours and the shape of a
 * dev status, which is the confusion this whole component exists to prevent. An unrecognised state is
 * printed as itself rather than bucketed into `open`.
 */
function StateChip({ state }: { state: string }) {
  const known = isKnownState(state);
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase ${
        known
          ? 'bg-[color:var(--color-line)] text-[color:var(--color-ink-muted)]'
          : 'border border-dashed border-[color:var(--color-line)] text-[color:var(--color-ink-muted)]'
      }`}
      title={known ? undefined : `Unrecognised state "${state}" — shown as sent by the API`}
    >
      {state}
    </span>
  );
}
